import React, { PropTypes } from 'react';

import { formatDateShorter } from '../../utils/formats';
import { googlePlaceShape } from '../../proptypes';

import Button from '../Button';
import DateRangePicker from '../../containers/DateRangePicker';
import Geopicker from '../Geopicker/Geopicker.react';
import Placepicker from '../Placepicker/Placepicker.react';
import PlacepickerPreset from '../PlacepickerPreset';
import PlacepickerPresetCountry from '../PlacepickerPreset/components/PlacepickerPresetCountry';

import { srOnly } from '../../base-styles/mixins.module.css';

import {
  actions,
  arrow,
  arrowField,
  arrowUp,
  button,
  checkbox,
  checkboxActive,
  container,
  containerClosed,
  conversational as convoStyle,
  conversationalField as convoField,
  count,
  edit,
  field,
  genericField,
  geopicker,
  input,
  inputEditing,
  inputEditingDate,
  label as labelStyle,
  learning,
  maskButton,
  maskVisual,
  option,
  options,
  optionActive,
  outerContainer,
  placeContainer,
  placepicker,
  preset,
  radius,
  radiusContainer,
  selection,
  suggested,
  toggle,
  toggleButton,
  value,
  onTop,
  headerSearch as headerSearchStyles
} from './styles.module.css';

/**
 * Event search filters presentational component.
 */
const EventSearchFilters = ({
  className,
  conversational,
  distances,
  editing,
  fetching,
  headerSearch,
  isOpen,
  onChange,
  onEdit,
  onFetched,
  onFetching,
  onSubmit,
  onToggleClick,
  sports,
  values,
  noBackdrop,
}) => (
  <div className={`${outerContainer} ${editing ? onTop : ''} ${headerSearch ? headerSearchStyles :  ''}`}>
    <form id="filters" className={[isOpen ? container : containerClosed, className].join(' ')}>
      {editing && !noBackdrop && <div className={maskVisual} />}
      {editing && !noBackdrop && <button type="button" className={maskButton} onClick={onSubmit} />}
      <div className={conversational ? convoField : field}>
        <div className={value}>
          {conversational && <span className={convoStyle}>I am </span>}
          {values.sport
            // Find the sport object for the given selection and return the label
            ? <span className={selection}>
              {sports.filter(s => s.id === values.sport)[0][conversational ? 'noun' : 'label']}</span>
            // Or just show default string, i.e. none specified
            : 'Sport'
          }
          <div className={arrowField} />
          <button type="button" name="sport" onClick={onEdit} className={edit}>
            <span className={srOnly}>{'Edit'}</span>
          </button>
        </div>
        {editing === 'sport' &&
          <div className={inputEditing}>
            <span className={labelStyle}>Sport</span>
            <div className={options}>
              {sports.map(({ id, label, noun }) => (
                <label
                  key={id}
                  htmlFor={`sport-${id}`}
                  className={values.sport === id ? optionActive : option}
                >
                  <input
                    type="radio"
                    id={`sport-${id}`}
                    name="sport"
                    onChange={onChange}
                    checked={values.sport ? values.sport === id : false}
                    value={id}
                  /> {conversational ? noun : label}
                </label>))}
            </div>
            <div className={actions}>
              <Button type="button" onClick={onSubmit} className={button}>{'Done'}</Button>
            </div>
          </div>}
      </div>

      {distances && <div className={conversational ? convoField : field}>
        <div className={value}>
          {conversational && <span className={convoStyle}>Looking for </span>}
          {(values.sportDistance && values.sportDistance.length)
            ? <span className={selection}>
              {values.sportDistance.map(selectedId =>
                // For each selection, find the matching distance object and return the label
                distances.filter(d => selectedId === d.id)[0].label)
                // Join the first two and show more count e.g. '+2', given length of selection array
                .slice(0, 2).join(', ') + (values.sportDistance.length > 2 ? `, +${values.sportDistance.length - 2} more` : '')}</span>
              // Or just show default string, i.e. none specified
            : <span>{'Distance'}</span>
          }
          <div className={arrowField} />
          {distances &&
            <button type="button" name="sportDistance" onClick={onEdit} className={edit}>
              <span className={srOnly}>{'Edit'}</span>
            </button>}
        </div>
        {(distances && editing === 'sportDistance') &&
          <div className={inputEditing}>
            <span className={labelStyle}>{'Distance'}</span>
            {distances &&
              <div className={options}>
                {distances.map(({ id, label }) => {
                  const name = `sportDistance-${id}`;
                  return (
                    <label
                      key={name}
                      htmlFor={`sportDistance-${id}`}
                      className={values.sportDistance &&
                        values.sportDistance.includes(id) ? checkboxActive : checkbox}
                    >
                      <input
                        type="checkbox"
                        id={name}
                        name={name}
                        onChange={onChange}
                        checked={values.sportDistance ? values.sportDistance.includes(id) : false}
                      /> {label}
                    </label>
                  );
                })}
              </div>}
            <div className={actions}>
              <Button type="button" onClick={onSubmit} className={button}>{'Done'}</Button>
            </div>
          </div>}
      </div>}
      {!headerSearch &&
      <div className={conversational ? convoField : field}>
        <div className={value}>
          {(!values.minDate && !values.maxDate)
            ? <span>Anytime</span> : <span>
              {values.minDate
                ? <span className={selection}>{formatDateShorter(values.minDate)}</span>
                : 'From'}
              {' → '}
              {values.maxDate
                ? <span className={selection}>{formatDateShorter(values.maxDate)}</span>
                : 'To'}
            </span>}
          <div className={arrowField} />
          <button type="button" name="date" onClick={onEdit} className={edit}>
            <span className={srOnly}>{'Edit'}</span>
          </button>
        </div>
        {editing === 'date' &&
          <div className={inputEditingDate}>
            <div className={labelStyle}>{'Dates'}</div>
            <div className={genericField}>
              <label htmlFor="from" className={srOnly}>{'From'}</label>
              <DateRangePicker
                startDate={values.minDate}
                endDate={values.maxDate}
                onChange={onChange}
              />
            </div>
            <div className={actions}>
              <Button type="button" onClick={onSubmit} className={button}>{'Done'}</Button>
            </div>
          </div>}
      </div>}

      <div className={conversational ? convoField : field}>
        <div className={value}>
          {conversational && <span className={convoStyle}>Near </span>}
          {values.location
            ? <span className={selection}>{values.location.name}</span>
            : 'Anywhere'}
          <div className={arrowField} />
          <button type="button" name="location" onClick={onEdit} className={edit}>
            <span className={srOnly}>{'Edit'}</span>
          </button>
        </div>
        {editing === 'location' &&
          <div className={inputEditing}>
            <label htmlFor="location" className={labelStyle}>{'Location'}</label>
            <div className={[genericField, options].join(' ')}>
              <div className={placeContainer}>
                <Placepicker
                  id="location"
                  name="location"
                  onChange={onChange}
                  className={placepicker}
                  value={values.location || null}
                />
              </div>
              {values.location
                && !['country', 'administrative_area_level_1', 'administrative_area_level_2', 'bounds'].includes(values.location.types[0]) &&
                <div className={radiusContainer}>
                  <div className={arrow} />
                  <select
                    id="locationSearchRadius"
                    name="locationSearchRadius"
                    className={radius}
                    onChange={onChange}
                    value={values.locationSearchRadius || ''}
                  >
                    <option value="">- Choose Radius -</option>
                    <option value="10">Within 10 km</option>
                    <option value="25">Within 25 km</option>
                    <option value="50">Within 50 km</option>
                    <option value="100">Within 100 km</option>
                    <option value="200">Within 200 km</option>
                  </select>
                </div>}
              <div className={suggested}>Suggested</div>
              <Geopicker
                id="geolocation"
                name="geolocation"
                label="Current Location"
                className={geopicker}
                checked={values.geolocation || false}
                onChange={onChange}
                onFetched={onFetched}
                onFetching={onFetching}
              />
            </div>

            <div className={actions}>
              <Button type="button" onClick={onSubmit} disabled={fetching} className={button}>{'Done'}</Button>
            </div>
          </div>}
      </div>
    </form>
    {!conversational &&
    <div className={toggle}>
      Filters <span className={count}>{[
        // Count number of defined filters
        values.sport,
        values.sportDistance && values.sportDistance[0],
        values.maxDate || values.minDate,
        values.location,
      ].filter(a => a).length}</span>
      <div className={isOpen ? arrowUp : arrow} />
      <a href="#filters" className={toggleButton} onClick={onToggleClick}>
        <span className={srOnly}>Show filters</span>
      </a>
    </div>}
  </div>
);

EventSearchFilters.propTypes = {
  /**
   * String class names to append to component styles.
   */
  className: PropTypes.string,
  /**
   * Adds conversation tone, i.e. `I am a X looking for Y near Z`.
   */
  conversational: PropTypes.bool,
  /**
   * Array of distances to render as checkbox inputs.
   */
  distances: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      label: PropTypes.string.isRequired,
    }).isRequired,
  ),
  /**
   * String name of field currently being edited.
   */
  editing: PropTypes.oneOf(['', 'sport', 'sportDistance', 'date', 'location']),
  /**
   * Bool describing state of required data fetch.
   */
  fetching: PropTypes.bool,
  /**
   * Bool to determine whether to render search field or not.
   */
  headerSearch: PropTypes.bool,
  /**
   * Boolean whether form is toggled open.
   */
  isOpen: PropTypes.bool,
  /**
   * Function called on any field change.
   */
  onChange: PropTypes.func,
  /**
   * Function called on field edit.
   */
  onEdit: PropTypes.func,
  /**
   * Function called on fetching required data, e.g. Geolocation.
   */
  onFetched: PropTypes.func,
  /**
   * Function called on response or error of required data, e.g. Geolocation.
   */
  onFetching: PropTypes.func,
  /**
   * Function called on form submit.
   */
  onSubmit: PropTypes.func,
  /**
   * Function called on form toggle.
   */
  onToggleClick: PropTypes.func,
  /**
   * Array of sports to render in select input.
   */
  sports: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      label: PropTypes.string.isRequired,
    }).isRequired,
  ).isRequired,
  /**
   * Object representing current form selections.
   */
  values: PropTypes.shape({
    geolocation: PropTypes.bool,
    location: googlePlaceShape,
    locationSearchRadius: PropTypes.number,
    maxDate: PropTypes.instanceOf(Date),
    minDate: PropTypes.instanceOf(Date),
    sport: PropTypes.number,
    sportDistance: PropTypes.arrayOf(
      PropTypes.number.isRequired,
    ),
  }),
};

EventSearchFilters.defaultProps = {
  className: '',
  conversational: false,
  distances: undefined,
  editing: undefined,
  fetching: false,
  headerSearch: false,
  isOpen: false,
  onChange: () => {},
  onEdit: () => {},
  onFetched: () => {},
  onFetching: () => {},
  onSubmit: () => {},
  onToggleClick: () => {},
  values: {},
};

export default EventSearchFilters;



// WEBPACK FOOTER //
// ./app/src/components/EventSearchFilters/index.jsx